import { cx } from '@emotion/css';
import { getConfig } from '@sortlist-frontend/config';
import { AdvancedMarker, APIProvider, Map, useApiIsLoaded, useMap } from '@vis.gl/react-google-maps';
import { useEffect, useRef, useState } from 'react';
import useDeepCompareEffect from 'use-deep-compare-effect';

import { findFirstOfficeIndex, type OfficeNode } from '_features/AgencyProfile/components/ContactPanel/utils';

import * as S from './styles';

interface Props {
  children?: never;
  agencyName: string;
  agencySlug: string;
  className?: string;
  selectedOffice: OfficeNode | null;
  offices: OfficeNode[];
}

const zoomSizes = {
  1: 200,
  2: 90,
  3: 40,
  4: 10,
  5: 8,
  6: 7,
  7: 6,
  8: 5,
  9: 4,
  10: 3,
  11: 2,
  12: 1,
};

const GoogleMap = (props: Props) => {
  const { offices, agencyName, agencySlug, selectedOffice, className } = props;
  const GoogleMapRef = useRef<HTMLDivElement>(null);
  const isLoadedMaps = useApiIsLoaded();
  const map = useMap();

  const [zoom, setZoom] = useState(11);
  const [center, setCenter] = useState({
    lng: 0,
    lat: 0,
  });

  useEffect(() => {
    if (isLoadedMaps && selectedOffice !== null) {
      const index = findFirstOfficeIndex(offices, selectedOffice);
      if (index !== null && map != null && selectedOffice.latitude != null && selectedOffice.longitude != null) {
        setCenter({
          lat: selectedOffice.latitude,
          lng: selectedOffice.longitude,
        });
        const bounds = new window.google.maps.LatLngBounds({
          lat: selectedOffice.latitude,
          lng: selectedOffice.longitude,
        });
        map.fitBounds(bounds);
        map.addListener('zoom_changed', () => {
          const currentZoom = map.getZoom();
          if (currentZoom != null && currentZoom > 15) {
            map.setZoom(15);
          }
        });
      }
    }
  }, [selectedOffice]);

  useDeepCompareEffect(() => {
    if (offices.length > 0) {
      const first = offices[0];
      if (first.longitude != null && first.latitude != null) {
        setCenter({
          lng: first.longitude,
          lat: first.latitude,
        });
      }
      if (offices.length > 1) {
        const longitudes = offices.filter((o) => o.longitude != null).map((o) => o.longitude as number);
        const latitudes = offices.filter((o) => o.latitude != null).map((o) => o.latitude as number);
        const latDiff = Math.max(...latitudes) - Math.min(...latitudes);
        const lngDiff = Math.max(...longitudes) - Math.min(...longitudes);

        Object.entries(zoomSizes).some(([key, value]) => {
          if (latDiff > value || lngDiff > value) {
            setZoom(Number(key));
            return true;
          }
          return false;
        });
      }
    }
  }, [offices]);

  useEffect(() => {
    const images = GoogleMapRef?.current?.getElementsByTagName('img');
    if (images != null) {
      for (let i = 0; i < images?.length; i++) {
        images[i].setAttribute('alt', `Google map - ${i}`);
      }
    }
  });

  return (
    <S.MapContainer ref={GoogleMapRef} className={cx(className, 'flex-gt-sm bg-secondary-300')}>
      <Map
        defaultCenter={center}
        defaultZoom={zoom}
        style={{ width: '100%', height: '100%' }}
        mapId="contact-google-map">
        {offices.map((office) =>
          office.latitude != null && office.longitude != null ? (
            <AdvancedMarker
              key={office.id}
              position={{ lat: office.latitude, lng: office.longitude }}
              title={office.locality?.name ?? undefined}
            />
          ) : null,
        )}
      </Map>
    </S.MapContainer>
  );
};

export const GoogleMapCtn = (props: Props) => {
  const { publicEnv } = getConfig();
  const { googlePlaceApiKey } = publicEnv;

  if (props.offices.length === 0) {
    return null;
  }

  return (
    <APIProvider apiKey={googlePlaceApiKey as string} libraries={['places']}>
      <GoogleMap {...props} />
    </APIProvider>
  );
};
